/**
 * SetCardsSummary - Text-based multi-column summary view for set cards
 *
 * Displays set cards grouped by type category in a compact, scannable format.
 * Uses masonry-style column assignment to balance section heights across columns.
 * Similar to DeckSummaryView but adapted for MinimalCard data with ownership highlighting.
 */
import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import { useLocalStorage, useWindowSize } from '@uidotdev/usehooks';
import clsx from 'clsx';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { IoCheckmark } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import { parseCollectorNumber, RARITY_ORDER } from '@/lib/sortConstants';
import type { MinimalCard } from '@/services/cardDatabase';

import { getRarityTextClass, getTypeCategory, TYPE_CATEGORY_LABELS } from '@/lib/cardUtils';

import { getTypeSortOrder } from '@/lib/cardGrouping';
import { SET_SORT_FIELD_LABELS, type SetSortField } from '../hooks/useSetSort';

// ============================================================================
// Types
// ============================================================================

export interface SetCardsSummaryProps {
  /** Cards in the set (pre-sorted by global sort) */
  cards: MinimalCard[];
  /** Map of scryfall_id -> owned quantity */
  ownedQuantities: Map<string, number>;
  /** Called when a card is clicked */
  onSelectCard?: (scryfallId: string) => void;
  /** Currently selected card ID */
  selectedCardId?: string | null;
  /** Global sort field (from toolbar) - used for within-section sorting */
  sortField?: SetSortField;
}

/** Card with ownership data for summary display */
interface SummaryCard {
  card: MinimalCard;
  ownedQuantity: number;
}

/** Category definition with sorting metadata */
interface CategoryGroup {
  id: string;
  title: string;
  cards: SummaryCard[];
  count: number;
  sortOrder: number;
}

// ============================================================================
// Sort Labels
// ============================================================================

// ============================================================================
// Masonry Column Distribution
// ============================================================================

/**
 * Distribute categories across columns using a "shortest column" strategy.
 * Each category is assigned to whichever column currently has the least total height.
 * Height is estimated as number of cards + 1 (for the header).
 */
function distributeToColumns(categories: CategoryGroup[], columnCount: number): CategoryGroup[][] {
  const columns: CategoryGroup[][] = Array.from({ length: columnCount }, () => []);
  const columnHeights: number[] = Array(columnCount).fill(0);

  for (const cat of categories) {
    // Find the shortest column
    let shortestIdx = 0;
    for (let i = 1; i < columnCount; i++) {
      if (columnHeights[i] < columnHeights[shortestIdx]) {
        shortestIdx = i;
      }
    }

    // Add category to shortest column
    columns[shortestIdx].push(cat);
    // Estimate height: header (1) + cards count
    columnHeights[shortestIdx] += 1 + cat.cards.length;
  }

  return columns;
}

// ============================================================================
// Sorting Logic
// ============================================================================

/**
 * Sort cards within a category based on the selected sort preference
 */
function sortCardsInGroup(cards: SummaryCard[], sortBy: SetSortField): SummaryCard[] {
  return [...cards].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.card.name.localeCompare(b.card.name);

      case 'cmc': {
        const cmcDiff = (a.card.cmc ?? 0) - (b.card.cmc ?? 0);
        if (cmcDiff !== 0) return cmcDiff;
        return a.card.name.localeCompare(b.card.name);
      }

      case 'collector_number': {
        const cnA = parseCollectorNumber(a.card.collector_number);
        const cnB = parseCollectorNumber(b.card.collector_number);
        const numDiff = cnA.num - cnB.num;
        if (numDiff !== 0) return numDiff;
        return cnA.suffix.localeCompare(cnB.suffix);
      }

      case 'rarity': {
        const rarityA = RARITY_ORDER[a.card.rarity ?? 'common'] ?? 99;
        const rarityB = RARITY_ORDER[b.card.rarity ?? 'common'] ?? 99;
        const rarityDiff = rarityB - rarityA; // Higher rarity first
        if (rarityDiff !== 0) return rarityDiff;
        return a.card.name.localeCompare(b.card.name);
      }

      case 'type':
        // Within a group, type sorting is less useful but we'll sort by type_line
        return (a.card.type_line ?? '').localeCompare(b.card.type_line ?? '');

      case 'colors': {
        // Sort by color identity length (mono < multi < colorless)
        const aColors = a.card.color_identity.length;
        const bColors = b.card.color_identity.length;
        const colorDiff = aColors - bColors;
        if (colorDiff !== 0) return colorDiff;
        return a.card.name.localeCompare(b.card.name);
      }

      case 'owned': {
        // Owned cards first, then by quantity
        const aOwned = a.ownedQuantity > 0 ? 1 : 0;
        const bOwned = b.ownedQuantity > 0 ? 1 : 0;
        const ownedDiff = bOwned - aOwned;
        if (ownedDiff !== 0) return ownedDiff;
        const qtyDiff = b.ownedQuantity - a.ownedQuantity;
        if (qtyDiff !== 0) return qtyDiff;
        return a.card.name.localeCompare(b.card.name);
      }

      default:
        return a.card.name.localeCompare(b.card.name);
    }
  });
}

/**
 * Group cards by type category
 */
function groupCardsByType(cards: MinimalCard[], ownedQuantities: Map<string, number>, sortBy: SetSortField): CategoryGroup[] {
  const groups = new Map<string, SummaryCard[]>();

  for (const card of cards) {
    const category = getTypeCategory(card.type_line ?? '');
    const summaryCard: SummaryCard = {
      card,
      ownedQuantity: ownedQuantities.get(card.id) ?? 0,
    };

    const existing = groups.get(category);
    if (existing) {
      existing.push(summaryCard);
    } else {
      groups.set(category, [summaryCard]);
    }
  }

  // Plural display titles for set summary categories
  const PLURAL_TITLES: Record<string, string> = {
    creature: 'Creatures',
    land: 'Lands',
    planeswalker: 'Planeswalkers',
    instant: 'Instants',
    sorcery: 'Sorceries',
    artifact: 'Artifacts',
    enchantment: 'Enchantments',
    battle: 'Battles',
    other: 'Other',
  };

  // Convert to CategoryGroup array with sorting
  const result: CategoryGroup[] = [];
  for (const [category, groupCards] of groups) {
    const sortedCards = sortCardsInGroup(groupCards, sortBy);
    result.push({
      id: category,
      title: PLURAL_TITLES[category] ?? TYPE_CATEGORY_LABELS[category as keyof typeof TYPE_CATEGORY_LABELS] ?? category,
      cards: sortedCards,
      count: sortedCards.length,
      sortOrder: getTypeSortOrder(category as Parameters<typeof getTypeSortOrder>[0]),
    });
  }

  // Sort groups by their sort order
  result.sort((a, b) => a.sortOrder - b.sortOrder);
  return result;
}

// ============================================================================
// Component
// ============================================================================

export default function SetCardsSummary({
  cards,
  ownedQuantities,
  onSelectCard,
  selectedCardId,
  sortField = 'collector_number',
}: SetCardsSummaryProps): ReactElement {
  // Use provided sort field from toolbar
  const effectiveSortField: SetSortField = sortField;

  // Collapsed sections (persisted)
  const [collapsedSections, setCollapsedSections] = useLocalStorage<Record<string, boolean>>('mtgcraft:set-summary-collapsed', {});

  const toggleCollapse = useCallback(
    (sectionId: string) => {
      setCollapsedSections((prev) => ({
        ...prev,
        [sectionId]: !prev[sectionId],
      }));
    },
    [setCollapsedSections],
  );

  // Group cards by type
  const categories = useMemo(
    () => groupCardsByType(cards, ownedQuantities, effectiveSortField),
    [cards, ownedQuantities, effectiveSortField],
  );

  // Calculate totals
  const totalCards = cards.length;
  const ownedCount = useMemo(() => {
    let count = 0;
    for (const card of cards) {
      if ((ownedQuantities.get(card.id) ?? 0) > 0) {
        count++;
      }
    }
    return count;
  }, [cards, ownedQuantities]);

  // Determine column count based on window width (max 3 columns for summary)
  const { width } = useWindowSize();
  const columnCount = useMemo(() => {
    if (!width || width < 768) return 1;
    if (width < 1024) return 2;
    return 3;
  }, [width]);

  // Distribute categories to columns using masonry (shortest column first)
  const columns = useMemo(() => distributeToColumns(categories, columnCount), [categories, columnCount]);

  // Render a category section
  const renderCategory = (cat: CategoryGroup) => {
    const isCollapsed = collapsedSections[cat.id] ?? false;
    return (
      <div key={cat.id} className="min-w-0">
        {/* Collapsible header */}
        <button
          type="button"
          onClick={() => toggleCollapse(cat.id)}
          className="mb-1 flex w-full items-center gap-1.5 text-xs font-semibold tracking-wider"
        >
          {isCollapsed ? <FaChevronRight className="h-2.5 w-2.5 text-zinc-500" /> : <FaChevronDown className="h-2.5 w-2.5 text-zinc-500" />}
          <span className="text-zinc-400 uppercase">{cat.title}</span>
          <span className="text-zinc-600">({cat.count})</span>
        </button>

        {/* Card list - flex-col forces block stacking since CardHoverPreview is inline-block */}
        {!isCollapsed && (
          <div className="flex flex-col gap-0.5">
            {cat.cards.map(({ card, ownedQuantity }) => {
              const isOwned = ownedQuantity > 0;
              const isSelected = selectedCardId === card.id;

              return (
                <CardHoverPreview key={card.id} imageUris={card.image_uris} name={card.name}>
                  <button
                    type="button"
                    onClick={() => onSelectCard?.(card.id)}
                    className={clsx(
                      'group flex w-full items-center gap-2 rounded px-1.5 py-0.5 text-left text-sm transition-colors',
                      'hover:bg-zinc-800/50 focus-visible:bg-zinc-800/50',
                      'focus-visible:ring-aether focus-visible:ring-1 focus-visible:outline-none',
                      isSelected && 'ring-aether/50 bg-zinc-800/70 ring-1',
                    )}
                  >
                    {/* Ownership indicator - fixed width to prevent layout shift */}
                    <span className="flex w-8 shrink-0 items-center justify-end">
                      {isOwned && (
                        <span className="flex items-center gap-0.5 text-emerald-400">
                          <IoCheckmark className="h-3.5 w-3.5" />
                          {ownedQuantity > 1 && <span className="text-xs font-medium">{ownedQuantity}</span>}
                        </span>
                      )}
                    </span>

                    {/* Card name with rarity color */}
                    <span className={clsx('flex-1 truncate', getRarityTextClass(card.rarity))}>{card.name}</span>

                    {/* Collector number */}
                    <span className="shrink-0 font-mono text-xs text-zinc-600">#{card.collector_number}</span>
                  </button>
                </CardHoverPreview>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="set-summary-view mx-auto max-w-6xl">
      {/* Header with totals and sort indicator */}
      <div className="mb-4 flex items-baseline justify-between border-b border-zinc-700 pb-2">
        <div className="flex items-baseline gap-3">
          <h2 className="text-sm font-semibold tracking-wider text-zinc-400 uppercase">Set Summary</h2>
          <span className="text-xs text-zinc-500">Sorted by: {SET_SORT_FIELD_LABELS[effectiveSortField]}</span>
        </div>
        <div className="flex items-baseline gap-4 text-sm">
          <span className="text-zinc-400">
            <span className="font-semibold text-zinc-200">{totalCards.toLocaleString()}</span> cards
          </span>
          <span className="text-zinc-400">
            <span className="font-semibold text-emerald-400">{ownedCount.toLocaleString()}</span> owned
          </span>
        </div>
      </div>

      {/* Masonry columns */}
      <div className="flex flex-col gap-6 md:flex-row md:gap-8 lg:gap-12">
        {columns.map((column, colIdx) => (
          <div key={colIdx} className="flex min-w-0 flex-1 flex-col gap-4">
            {column.map((cat) => renderCategory(cat))}
          </div>
        ))}
      </div>
    </div>
  );
}
